'use client';

import { type StockItemResult } from '@app/(darkHeader)/ai/_shared/_actions/fetchStockItem';
import { AiToolkitVideoPlayer } from '@app/(darkHeader)/ai/_shared/_components/AiToolkitVideoPlayer/AiToolkitVideoPlayer';
import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { failureReasonContent, VIDEO_SEARCH_MEDIA_TYPE_OPTIONS } from '@app/(darkHeader)/ai/_shared/_context/constants';
import { canRetry } from '@app/(darkHeader)/ai/_shared/_context/failureReason';
import { AI_TOOLKIT_CATEGORIES } from '@app/(darkHeader)/ai/_shared/_context/types';
import { AiToolkitMainPanel } from '@app/(darkHeader)/ai/_shared/_layouts/AiToolkitMainPanel';
import { GenerationFlowErrorLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowErrorLayout';
import { GenerationFlowIdleView } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowIdleView';
import { GenerationFlowInProgressLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowInProgressLayout';
import { type JSX, useState } from 'react';

import { Button, cn, ProminentSearch } from '@videoblocks/design-system';
import { AiWand } from '@videoblocks/design-system/icons';

import { type VideoEditDeepLink } from '../../_utils/videoEditDeepLink';
import { useVideoEditForm } from '../useVideoEditForm';
import { VideoSearchLayout } from './Search/VideoSearchLayout';
import { VideoEditSelectionView } from './VideoEditSelectionView/VideoEditSelectionView';
import { VideoEditSuccessView } from './VideoEditSuccessView/VideoEditSuccessView';
import { useApplyVideoEditDeepLink } from './useApplyVideoEditDeepLink';

export type VideoEditMainPanelProps = {
    /** The id a StockBlocks "Edit with AI" button linked here with, if any. */
    deepLink?: VideoEditDeepLink | null;
    /** That id resolved server-side, or null when the link is dead. */
    deepLinkItem?: StockItemResult | null;
};

export const VideoEditMainPanel = ({ deepLinkItem = null }: VideoEditMainPanelProps): JSX.Element => {
    const { submitStates, videoSearch } = useAiToolkitContext();
    const { searchTerm, setSearchTerm, search, showSearch, setShowSearch } = videoSearch;
    const { onConfirm, isValid } = useVideoEditForm();

    useApplyVideoEditDeepLink(deepLinkItem);

    const [showSearchClose, setShowSearchClose] = useState(searchTerm.length > 0);

    const { status, operation, originalThumbnailUrl, failureReason } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT];
    const showSelection = status === 'selection';
    const isLoading = status === 'inProgress';
    const isSuccess = status === 'success';
    const isError = status === 'error';

    const errorContent = failureReason ? failureReasonContent(failureReason) : undefined;
    const errorView = (
        <GenerationFlowErrorLayout
            title={errorContent?.title}
            subtitle={errorContent?.subtitle}
            canRetry={canRetry(failureReason)}
            onConfirm={() => void onConfirm(operation)}
            isValid={isValid}
        />
    );

    const inProgressView = (
        <GenerationFlowInProgressLayout className="h-auto gap-6 justify-start">
            <AiToolkitVideoPlayer
                state="loading"
                thumbnailUrl={originalThumbnailUrl}
                thumbnailAlt="Preview for your video edit is loading..."
                src=""
            />
        </GenerationFlowInProgressLayout>
    );

    return (
        <AiToolkitMainPanel
            heading="Video Editor"
            icon={<AiWand width={16} height={16} />}
            className={cn(showSearch && 'max-h-[55vh] md:max-h-[calc(100vh-132px)]')}
            innerContainerClassName={cn(showSearch && 'w-full 2xl:max-w-auto 2xl:p-4')}
        >
            <div className={cn('flex flex-col gap-5 h-full', showSearch && 'gap-4')}>
                <div className="flex gap-2 w-full">
                    <ProminentSearch
                        selectOptions={VIDEO_SEARCH_MEDIA_TYPE_OPTIONS}
                        defaultSelectValue={VIDEO_SEARCH_MEDIA_TYPE_OPTIONS[0].value}
                        value={searchTerm}
                        onChange={(e) => {
                            setShowSearch(true);
                            setSearchTerm(e.target.value);
                            setShowSearchClose(e.target.value.trim().length > 0);
                        }}
                        placeholder="Search for footage to edit..."
                        onSubmit={() => {
                            void search();
                            setShowSearch(true);
                        }}
                        showClose={showSearchClose}
                        onClose={() => {
                            setSearchTerm('');
                            setShowSearchClose(false);
                        }}
                        mediaSelectProps={{
                            disabled: true,
                        }}
                    />
                </div>
                {showSearch ? (
                    <VideoSearchLayout />
                ) : isError ? (
                    errorView
                ) : showSelection ? (
                    <VideoEditSelectionView
                        fallbackVideoUrl={deepLinkItem?.previewUrl}
                        fallbackAspectRatio={deepLinkItem?.aspectRatio}
                    />
                ) : isLoading ? (
                    inProgressView
                ) : isSuccess ? (
                    <VideoEditSuccessView />
                ) : (
                    <GenerationFlowIdleView
                        title="Edit videos with AI"
                        subtitle="Modify a video from our library using a prompt."
                    >
                        <Button
                            variant="secondary"
                            className="w-fit"
                            onClick={() => {
                                setShowSearch(true);
                                void search();
                            }}
                        >
                            Browse Videos
                        </Button>
                    </GenerationFlowIdleView>
                )}
            </div>
        </AiToolkitMainPanel>
    );
};
